import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { getProductByID } from '@adornis/buchungsheld-db/api/product.js';
import { isSDInternMail } from './sd-email.js';
export { isSDInternMail } from './sd-email.js';
import { paymentPluginSdPay, getSDInvoiceByID } from '../api/sd-payment-plugin.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { getCurrentUserServer, saveEntityData } from '@adornis/bh-util/util.js';
import { getParticipationByID, getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getExistingStammdaten } from '@adornis/buchungsheld-db/api/stammdaten.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { BHInvoice } from '@adornis/buchungsheld-db/entities/BHInvoice.js';
import type { BHPayment } from '@adornis/buchungsheld-db/entities/BHPayment.js';
import { Einzelberatung, EinzelberatungSlotConfiguration } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { Price } from '@adornis/payments/db/prices.js';
import { strictEqual } from 'assert';
import { addDays, format, isBefore, subDays } from 'date-fns';
import {
  SDHessenOccupations,
  SDInstitutionEnum,
  SDOccupationEnum,
  SDStammdaten,
} from '../stammdaten/sd-stammdaten-plugin.js';
import { SD_INVOICE_CONFIG } from './sd-invoice-config.js';
import { SDInvoiceReceiverEnum } from './invoice-receiver.js';
import { defaultPaymentPlugin } from './default.js';

@Entity()
export class SDInvoice extends BHInvoice {
  static override _class = 'SDInvoice';

  // pdf file path of interal-invoice version for accouning
  @Field(type => String)
  pdfInternalID: Maybe<string>;

  @Field(type => String)
  ilvFormularID: Maybe<string>;

  // somtimes receiver should not be the user
  @Field(type => String)
  mailReceiver: Maybe<string>;
}

const isHessen = (occupation: SDOccupationEnum) => SDHessenOccupations.includes(occupation);

const getProductPriceLogic = (
  product: NarrowedEntityData<Product, { prices: { _id: 1 } }>,
  user: NarrowedEntityData<User, { _id: 1; email: 1 }>,
  stammdaten: NarrowedEntityData<SDStammdaten, { occupation: 1; department: 1 }>,
) => {
  const prices = product?.prices;
  if (!prices) return null;

  const email = user.email;
  const occupation = stammdaten.occupation;
  const department = stammdaten.department;

  if (!occupation) throw new Error('occupation not set; cant calculate price');
  if (!department) throw new Error('department not set; cant calculate price');

  if (isSDInternMail(email) || [SDInstitutionEnum.SDMEDIZIN, SDInstitutionEnum.SDUNIKLINIKUM].includes(department))
    return prices[0]; // intern
  if (isHessen(occupation)) return prices[1]; // hessen
  return prices[2]; // extern
};

export const getProductPrice = async (productID: string, userID: string) => {
  const product = await getProductByID(productID)({
    institutionID: 1,
    prices: { ...selectionSet(() => Price, 2), _id: 1, unitAmount: 1, tax_behavior: 1, currency: 1 },
  });
  if (!product) throw new Error('could not find product ' + productID);

  const user = await getUserByID(userID)({ _id: 1, firstName: 1, lastName: 1, title: 1, email: 1 });
  if (!user) throw new Error('could not find user ' + userID);

  const stammdaten = await getExistingStammdaten<SDStammdaten>(
    user._id,
    product.institutionID,
    null,
  )({ occupation: 1, department: 1 });

  if (!user) throw new Error('user not found ' + userID);
  if (!stammdaten) throw new Error('stammdaten not found user=' + userID + ', institution=' + product.institutionID);

  return getProductPriceLogic(product, user, stammdaten) as Price | null;
};

export function sdTimeSlot(from: Date, till: Date) {
  return [from, till].map(date => format(date, 'dd.MM.yyyy HH:mm')).join(' - ');
}

export const sdPaymentPlugin: PaymentPlugin<'invoice', 'intern' | 'hessen' | 'extern'> = {
  id: 'payment::sd',
  type: 'payment',
  paymentMethods: ['invoice'],
  internalPaymentExportConfig: {
    invoiceReceiverEnum: SDInvoiceReceiverEnum.SDINTERNAL,
    costCenterReceiver: SD_INVOICE_CONFIG.intern.invoiceProjectNumber,
    kostenart: '91100100',
  },
  getProductPrice,
  createPayment: async (participationIDs: string[], _paymentMethod) => {
    const user = getCurrentUserServer();
    if (!user) throw new Error('user not found');

    const participations = await getParticipationsByID(participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const institutionID = participations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    const paymentID = await paymentPluginSdPay(institutionID, participations)();
    return [paymentID];
  },
  pay: async (paymentID, _paymentMethod) => {
    // nothing to do here; invoice gets paid manually by user
  },

  afterEnrollment: async participationID => {},

  sendInvoice: async (invoiceID: string): Promise<boolean> => {
    const invoice = await getSDInvoiceByID(invoiceID)({
      _id: 1,
      paymentID: 1,
      pdfID: 1,
      pdfInternalID: 1,
      ilvFormularID: 1,
      mailReceiver: 1,
    });
    if (!invoice) throw new Error('invoice not found');

    // const payment = await getBHPaymentByID(invoice.paymentID)({
    //   _id: 1,
    //   createdAt: 1,
    //   participationIDs: 1,
    //   amount: 1,
    //   status: 1,
    //   orderNr: { year: 1, nr: 1, type: 1 },
    // });
    // NOTE: deactivate invoice for now (we currently use semester invoices)
    return true;
    // if (!payment) throw new Error('should not happen');

    // const participations = await getParticipationsByID(payment.participationIDs)({
    //   _id: 1,
    //   userID: 1,
    //   user: {
    //     _id: 1,
    //     email: 1,
    //     firstName: 1,
    //     lastName: 1,
    //     title: 1,
    //     username: 1,
    //     whatsappNotification: 1,
    //     whatsappPhone: 1,
    //     suspentions: { _id: 1, institutionID: 1 },
    //     receivesNewsletter: 1,
    //   },
    //   productID: 1,
    //   product: {
    //     _id: 1,
    //     name: 1,
    //     institutionID: 1,
    //     hasPrice: 1,
    //     prices: { unitAmount: 1, currency: 1 },
    //   },
    //   createdAt: 1,
    //   updatedAt: 1,
    //   state: 1,
    //   isNoShow: 1,
    //   signoffDate: 1,
    //   registrationDate: 1,
    //   fulfilledParticipationRequirements: 1,
    //   lastStateChangeWasInitiatedByAdmin: 1,
    //   currentPayment: {
    //     status: 1,
    //     participationIDs: 1,
    //     orderNr: { year: 1, nr: 1 },
    //     amount: 1,
    //     discount: 1,
    //   },
    //   productPriceForUser: { unitAmount: 1, currency: 1 },
    // }).then(ps => ps.filter(isDefined));

    // const doNotSendInvoice = participations.every(p => !!p.ikh_doNotSendInvoice);
    // if (doNotSendInvoice) return false;

    // const p = participations[0];
    // if (!p) throw new Error('participations required');

    // if (p.state === ParticipationState.waiting) {
    //   logger.debug('invoice not send because user is on waiting list');
    //   return false;
    // }

    // const user = p.user;
    // if (!user) throw new Error('user is missing');

    // const product = p.product;
    // if (!product) throw new Error('product not found');

    // const institutionID = product.institutionID;

    // const collection = await getCollection<SDStammdaten>(UserStammdaten._class);
    // const stammdaten = await collection.findOne({ userID: user._id, institutionID });
    // if (!stammdaten) throw new Error('stammdaten not found');

    // const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    //   institutionID,
    //   MailTemplateType.SD_INVOICE_USER,
    // )(InstitutionMailTemplate.allFields);

    // let emailVars = (() => {
    //   if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
    //   else if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
    //   else return getEmailVarsFromUser(user);
    // })();

    // const token = await generateLoginToken({
    //   userID: user._id,
    //   userClass: User._class,
    //   ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
    // });

    // emailVars[MailTemplateVars.LOGIN_LINK] = InstitutionMailTemplate.mapLoginLinkVar(token);

    // const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: emailVars });

    // if (!mailContent) throw new Error('could not build email');
    // await sendInstitutionEmail({
    //   email: invoice.mailReceiver ?? user.email,
    //   subject: mailTemplate.subject,
    //   content: mailContent,
    //   institutionID,
    //   attachmentPaths: [
    //     A.absoluteUrl(AdornisFile.getServeLink(invoice.pdfID) + '/Rechnung.pdf'),
    //     ...(invoice.ilvFormularID ? [AdornisFile.getServeLink(invoice.ilvFormularID) + '/ilv.pdf'] : []),
    //   ],
    // });

    // // institution mails
    // if (invoice.pdfInternalID) {
    //   const s = p.product?.settings;
    //   if (!s) {
    //     logger.warn('no settings found; cant send accounting mails');
    //     return true;
    //   }

    //   const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    //     institutionID,
    //     MailTemplateType.SD_INVOICE_INSTITUTION,
    //   )(InstitutionMailTemplate.allFields);

    //   let emailVars = (() => {
    //     if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
    //     else if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
    //     else return getEmailVarsFromUser(user);
    //   })();

    //   const { content: mailContent } = await assembleInstitutionMail({ template: mailTemplate, infos: emailVars });
    //   if (!mailContent) throw new Error('could not build email');

    //   const institutionMail1 = s.interAccountingEmail1 ?? s.inheritedInterAccountingEmail1?.[1];
    //   const institutionMail2 = s.interAccountingEmail2 ?? s.inheritedInterAccountingEmail2?.[1];
    //   const institutionMail3 = s.interAccountingEmail3 ?? s.inheritedInterAccountingEmail3?.[1];
    //   const institutionMails = [institutionMail1, institutionMail2, institutionMail3]
    //     .filter(it => !!it)
    //     .filter(isDefined);

    //   if (institutionMails.length === 0) {
    //     logger.warn('no institution-mail available; cant send institution-invoice-copy');
    //     return true;
    //   }
    //   for (const institutionMail of institutionMails) {
    //     await sendInstitutionEmail({
    //       email: institutionMail,
    //       subject: mailTemplate.subject,
    //       content: mailContent,
    //       institutionID,
    //       attachmentPaths: [A.absoluteUrl(AdornisFile.getServeLink(invoice.pdfInternalID) + '/Rechnung.pdf')],
    //     });
    //   }
    // }

    // payment.status = 'issued';
    // await payment.save();

    // return true;
  },
  cancleEnrollment: async (participationID: string) => {
    const paymentGqlFields = {
      _id: 1,
      _class: 1,
      participationIDs: 1,
      status: 1,
      cancelReason: 1,
      discount: 1,
    } as const;
    const participation = await getParticipationByID(participationID)({
      currentPayment: paymentGqlFields,
      product: {
        _id: 1,
        ...{ appointments: { start: 1, end: 1 }, slotConfig: selectionSet(() => EinzelberatungSlotConfiguration, 2) },
      },
    });
    if (!participation?.currentPayment) return;

    const payment = participation.currentPayment as NarrowedEntityData<BHPayment, typeof paymentGqlFields>;
    switch (payment.status) {
      case 'paid': {
        const now = new Date();
        const firstTermin = await (async () => {
          const product = participation.product;
          if (product instanceof Workshop) return product.start;
          if (product instanceof Einzelberatung) return product.slotConfig?.start;
          throw new Error('unreachable');
        })();

        if (!firstTermin || isBefore(now, subDays(firstTermin, 3))) {
          // free cancelation
          payment.status = 'refund';
          payment.cancelReason = 'signoff';
          await saveEntityData(payment);
          logger.info({ payment }, 'cancle payment free of charge (sd)');
        } else if (isBefore(now, addDays(firstTermin, 7))) {
          payment.discount = 40; // pay 60%
          await saveEntityData(payment);
          logger.info({ payment }, 'cancle payment free of charge (sd)');
        } else void 0; // do nothing (user has to pay full price)
        return;
      }
      default:
        await defaultPaymentPlugin.cancleEnrollment(participationID);
        return;
    }
  },

  priceGroups: ['intern', 'hessen', 'extern'],
};

export function testSDPaymentPlugin() {
  describe('ikh-payment-plugin', () => {
    it('get intern price when user has uni-frankfurt mail', () => {
      const resultPrice = getProductPriceLogic(
        new Product({
          prices: [new Price({ _id: 'intern' }), new Price({ _id: 'hessen' }), new Price({ _id: 'extern' })],
        }),
        new User({ email: 'foo@bar.uni-frankfurt.de' }),
        new SDStammdaten({ occupation: SDOccupationEnum.SDHIWI_GOETHE, department: SDInstitutionEnum.SDGEO }),
      );

      strictEqual(resultPrice?._id, 'intern');
    });

    it('get hessen price when user is teacher', () => {
      const resultPrice = getProductPriceLogic(
        new Product({
          prices: [new Price({ _id: 'intern' }), new Price({ _id: 'hessen' }), new Price({ _id: 'extern' })],
        }),
        new User({ email: 'foo@bar.de' }),
        new SDStammdaten({ occupation: SDOccupationEnum.SDTEACHER, department: SDInstitutionEnum.SDEVANG_THEOLOGIE }),
      );

      strictEqual(resultPrice?._id, 'hessen');
    });

    it('get hessen price when user is hess hochschul angegoriger', () => {
      const resultPrice = getProductPriceLogic(
        { prices: [{ _id: 'intern' }, { _id: 'hessen' }, { _id: 'extern' }] },
        new User({ email: 'foo@bar.de' }),
        new SDStammdaten({
          occupation: SDOccupationEnum.SDHESS_HOCHSCHULANGEHOERIGE,
          department: SDInstitutionEnum.SDINFORMATIK,
        }),
      );

      strictEqual(resultPrice?._id, 'hessen');
    });
  });
}
